import Alert from '@codegouvfr/react-dsfr/Alert';
import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import clsx from 'clsx';
import { partition } from 'lodash-es';
import { Brand } from 'maestro-shared/constants';
import type { UserListItem } from 'maestro-shared/schema/User/User';
import { toMaestroDate } from 'maestro-shared/utils/date';
import { includesIgnoringAccents } from 'maestro-shared/utils/utils';
import { useCallback, useContext, useMemo, useState } from 'react';
import usersSvg from 'src/assets/illustrations/users.svg';
import { AppPage } from 'src/components/_app/AppPage/AppPage';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useAuthentication } from '../../hooks/useAuthentication';
import { ApiClientContext } from '../../services/apiClient';
import { UserCard } from './components/UserCard';
import { UserModal } from './components/UserModal';
import { type FindUserOptions, UsersFilters } from './components/UsersFilters';
import { useUserManagement } from './useUserManagement';

const userFormModal = createModal({
  id: `user-form-modale-id`,
  isOpenedByDefault: false
});

const confirmDisablingUserModal = createModal({
  id: `user-confirm-disabling-modale-id`,
  isOpenedByDefault: false
});

export const UserListView = () => {
  const apiClient = useContext(ApiClientContext);
  const { canManageUser } = useUserManagement();
  const { hasRole, account } = useAuthentication();

  const { data: allUsers } = apiClient.useFindUsersQuery({});

  const users = useMemo(() => {
    const visibleUsers = (allUsers ?? []).filter(
      (user) => user.id === account?.id || canManageUser(user)
    );
    const sortedUsers = hasRole('AdministratorMaestro')
      ? visibleUsers.toSorted(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        )
      : visibleUsers;
    const [myself, others] = partition(
      sortedUsers,
      (user) => user.id === account?.id
    );
    return [...myself, ...others];
  }, [allUsers, canManageUser, hasRole, account]);
  const { data: programmingPlans = [] } =
    apiClient.useFindProgrammingPlansQuery({});

  const [updateUser] = apiClient.useUpdateUserMutation();
  const [updateUserCertification] =
    apiClient.useUpdateUserCertificationMutation();

  const [userToUpdate, setUserToUpdate] = useState<null | UserListItem>(null);
  const [userToDisable, setUserToDisable] = useState<null | UserListItem>(null);
  const [alertMessage, setAlertMessage] = useState<null | string>(null);

  const [usersFiltered, setUsersFiltered] = useState<UserListItem[]>(users);

  const onEdit = async (userToEdit: UserListItem) => {
    setUserToUpdate({ ...userToEdit });
    userFormModal.open();
  };

  const onDisable = (userToDisable: UserListItem) => {
    setUserToDisable(userToDisable);
    confirmDisablingUserModal.open();
  };

  const onConfirmDisable = async () => {
    if (userToDisable) {
      await updateUser({
        ...userToDisable,
        userId: userToDisable.id,
        disabled: true
      });
      setAlertMessage(
        `L'utilisateur ${userToDisable.name} a bien été désactivé.`
      );
      setUserToDisable(null);
    }
  };

  const onEnableUser = async (userToEnable: UserListItem) => {
    await updateUser({
      ...userToEnable,
      userId: userToEnable.id,
      disabled: false
    });
    setAlertMessage(`L'utilisateur ${userToEnable.name} a bien été activé.`);
  };

  const onToggleCertification = async (user: UserListItem) => {
    const certified = !user.certified;
    await updateUserCertification({ userId: user.id, certified });
    setAlertMessage(
      certified
        ? `L'utilisateur ${user.name ?? user.email} a bien été marqué comme formé.`
        : `L'utilisateur ${user.name ?? user.email} a bien été marqué comme non formé.`
    );
  };

  const updateUsersFiltered = useCallback(
    (filters: FindUserOptions) => {
      setUsersFiltered(
        users.filter((u) => {
          if (filters.onlyDisabled && !u.disabled) {
            return false;
          }
          if (!filters.onlyDisabled && u.disabled) {
            return false;
          }

          if (filters.onlyUncertified && u.certified) {
            return false;
          }

          if (
            filters.createdFrom &&
            toMaestroDate(u.createdAt) < filters.createdFrom
          ) {
            return false;
          }

          if (
            filters.createdTo &&
            toMaestroDate(u.createdAt) > filters.createdTo
          ) {
            return false;
          }

          if (
            filters.label &&
            filters.label !== '' &&
            !includesIgnoringAccents(u.name ?? '', filters.label) &&
            !includesIgnoringAccents(u.email, filters.label)
          ) {
            return false;
          }

          if (
            filters.regions?.length &&
            u.region &&
            !filters.regions.includes(u.region)
          ) {
            return false;
          }

          if (
            filters.departments?.length &&
            (!u.department || !filters.departments.includes(u.department))
          ) {
            return false;
          }

          if (
            filters.roles?.length &&
            !filters.roles.some((role) => u.roles.includes(role))
          ) {
            return false;
          }

          if (
            filters.stages?.length &&
            !filters.stages.some((stage) => u.stages.includes(stage))
          ) {
            return false;
          }

          return true;
        })
      );
    },
    [users]
  );

  return (
    <>
      <AppPage
        title="Utilisateurs"
        subtitle={`Gestion des utilisateurs`}
        illustration={usersSvg}
        documentTitle="Gestions des utilisateurs"
        action={
          <Button
            onClick={() => {
              setUserToUpdate(null);
              userFormModal.open();
            }}
            iconId="fr-icon-microscope-line"
          >
            Ajouter un utilisateur
          </Button>
        }
      >
        <UsersFilters onChange={updateUsersFiltered} />

        <div
          className={clsx(
            'white-container',
            cx('fr-px-2w', 'fr-px-md-5w', 'fr-py-2w', 'fr-py-md-5w')
          )}
        >
          {alertMessage && (
            <Alert
              severity="success"
              small
              description={alertMessage}
              closable={true}
              className={cx('fr-mb-3w')}
              onClose={() => setAlertMessage(null)}
            />
          )}
          <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
            {usersFiltered.map((user) => (
              <div className={cx('fr-col-12', 'fr-col-md-4')} key={user.id}>
                <UserCard
                  user={user}
                  onEdit={() => onEdit(user)}
                  onDisable={() => onDisable(user)}
                  onEnable={() => onEnableUser(user)}
                  onToggleCertification={() => onToggleCertification(user)}
                />
              </div>
            ))}
          </div>
        </div>
      </AppPage>
      <UserModal
        modal={userFormModal}
        userToUpdate={userToUpdate}
        setAlertMessage={setAlertMessage}
        programmingPlans={programmingPlans}
      />
      <ConfirmationModal
        modal={confirmDisablingUserModal}
        title="Veuillez confirmer cette action"
        onConfirm={() => onConfirmDisable()}
        closeOnConfirm
      >
        Vous êtes sur le point de désactiver l'utilisateur{' '}
        <b>{userToDisable?.name}</b>, celui-ci n'aura plus accès à {Brand}.
      </ConfirmationModal>
    </>
  );
};
